import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { ArrowBack, PictureAsPdf, Email, Check } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { protocolAPI } from '../../services/api';

export default function ProtocolSummaryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const formData = location.state?.formData;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pdfUrl, setPdfUrl] = useState('');
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState(formData?.email || '');

  if (!formData) {
    return (
      <Container>
        <Alert severity="error">Keine Formulardaten gefunden</Alert>
        <Button onClick={() => navigate('/dashboard')}>Zurück</Button>
      </Container>
    );
  }

  const handleGeneratePDF = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await protocolAPI.create(formData);
      if (result.success) {
        setPdfUrl(protocolAPI.getPDF(result.pdf_id));
        setSuccess('PDF erfolgreich erstellt!');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Erstellen des PDFs');
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!pdfUrl) {
      setError('Bitte erst PDF generieren');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Email sending is handled by backend when creating protocol
      setSuccess('E-Mail erfolgreich versendet!');
      setShowEmailDialog(false);
      
      // Navigate back after success
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err) {
      setError('Fehler beim Versenden der E-Mail');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate(-1)}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Protokoll-Zusammenfassung
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            Wartungsprotokoll - {formData.kunde}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Kundennummer: {formData.ku_nr}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Datum: {formData.datum}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Hersteller: {formData.hersteller} • Innengeräte: {formData.innengeraete.length}
          </Typography>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Nächste Schritte
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Generieren Sie das PDF und versenden Sie es per E-Mail
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={loading ? <CircularProgress size={20} /> : <PictureAsPdf />}
              onClick={handleGeneratePDF}
              disabled={loading || pdfUrl}
            >
              {pdfUrl ? 'PDF erstellt ✓' : 'PDF generieren'}
            </Button>

            {pdfUrl && (
              <>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => window.open(pdfUrl, '_blank')}
                >
                  PDF ansehen
                </Button>

                <Button
                  variant="contained"
                  size="large"
                  color="success"
                  startIcon={<Email />}
                  onClick={() => setShowEmailDialog(true)}
                >
                  Per E-Mail versenden
                </Button>
              </>
            )}

            <Button
              variant="text"
              onClick={() => navigate('/dashboard')}
            >
              Zurück zum Dashboard
            </Button>
          </Box>
        </Paper>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <CircularProgress />
          </Box>
        )}
      </Container>

      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onClose={() => setShowEmailDialog(false)}>
        <DialogTitle>E-Mail versenden</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Das PDF wird an folgende Adressen gesendet:
          </Typography>
          <Typography><strong>Kunde:</strong> {formData.email}</Typography>
          <Typography><strong>CC:</strong> info@split-klimaanlagen.com</Typography>
          <Typography><strong>CC:</strong> gpt@split-klimaanlagen.com</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEmailDialog(false)}>Abbrechen</Button>
          <Button onClick={handleSendEmail} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Senden'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}